import { QRCodeSVG } from 'qrcode.react';

import { Logo } from 'src/Components/Logo';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import { FALLBACK_HOUSEHOLD_COLOR } from 'src/Utils/eventVisuals';

import { Props } from './PrintableStaysTimeline.types';

const INK = '#1B1B1B';
const STONE = '#4A4A4A';
const BORDER = '#1B1B1B';
const WEEKEND = 'rgba(0,0,0,0.05)';
const SANS = "'Inter', Helvetica, Arial, sans-serif";

const PrintableStaysTimeline = ({
  events,
  selectedDate,
  assetName,
  exportUrl,
  dateRangeLabel,
  printedOnLabel,
  todaySummary,
}: Props) => {
  const start = selectedDate.startOf('month');
  const daysInMonth = selectedDate.daysInMonth();
  const days = Array.from({ length: daysInMonth }, (_, i) => start.add(i, 'day'));

  const sejourEvents = events.filter(
    e => e.type === EventDtoTypeEnum.Occupation || e.type === EventDtoTypeEnum.Location
  );

  const householdMap = new Map<string, { id: string; name: string; color: string }>();
  sejourEvents.forEach(e => {
    if (e.household && !householdMap.has(e.household.id)) {
      householdMap.set(e.household.id, {
        id: e.household.id,
        name: e.household.name,
        color: e.household.color ?? FALLBACK_HOUSEHOLD_COLOR,
      });
    }
  });
  const households = Array.from(householdMap.values()).sort((a, b) => a.name.localeCompare(b.name));

  const publicHolidays = new Set(
    events
      .filter(e => e.type === EventDtoTypeEnum.PublicHoliday)
      .map(e => e.startTime.format('YYYY-MM-DD'))
  );

  const gridTemplateColumns = `150px repeat(${daysInMonth}, 1fr)`;

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns,
    borderBottom: `1px solid ${BORDER}`,
    minHeight: '32px',
    position: 'relative',
  };

  const dayCellStyle = (dayIdx: number, isWeekend: boolean): React.CSSProperties => ({
    borderRight: dayIdx < daysInMonth ? `1px solid ${BORDER}20` : 'none',
    background: isWeekend ? WEEKEND : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    color: INK,
  });

  const rowLabelStyle: React.CSSProperties = {
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '11px',
    fontWeight: 700,
    color: INK,
    borderRight: `1.5px solid ${BORDER}`,
  };

  const ribbonStyleFor = (color: string): React.CSSProperties => ({
    position: 'absolute',
    alignSelf: 'center',
    top: '4px',
    bottom: '4px',
    background: color,
    color: '#FFFFFF',
    borderRadius: '3px',
    padding: '0 6px',
    fontSize: '10px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    border: `1px solid ${BORDER}`,
  });

  return (
    <div style={{ width: '100%', fontFamily: SANS, color: INK, background: '#fff' }}>
      <div
        style={{
          padding: '14px 20px 10px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 20,
          borderBottom: `1.5px solid ${BORDER}`,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Logo variant="wordmark" alt="ShareHeritage" style={{ height: 22, width: 'auto' }} />
          <span style={{ fontSize: 10, color: STONE, letterSpacing: 0.5 }}>
            {printedOnLabel} · {todaySummary}
          </span>
        </div>
        <span
          style={{
            fontSize: 22,
            fontWeight: 700,
            textAlign: 'center',
            textTransform: 'capitalize',
          }}
        >
          {dateRangeLabel}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: STONE,
            letterSpacing: 1,
            textTransform: 'uppercase',
            textAlign: 'right',
          }}
        >
          {assetName}
        </span>
      </div>

      <div style={{ padding: '10px 20px 0' }}>
        {/* Day header */}
        <div style={rowStyle}>
          <div style={rowLabelStyle}>Séjours</div>
          {days.map((d, idx) => {
            const dow = d.day();
            const isWeekend = dow === 0 || dow === 6;
            const isPublicHoliday = publicHolidays.has(d.format('YYYY-MM-DD'));
            return (
              <div key={d.format('YYYY-MM-DD')} style={dayCellStyle(idx, isWeekend)}>
                <span
                  style={{
                    fontWeight: isPublicHoliday ? 700 : 500,
                    textDecoration: isPublicHoliday ? 'underline' : 'none',
                  }}
                >
                  {d.date()}
                </span>
              </div>
            );
          })}
        </div>

        {/* One row per household */}
        {households.map(h => {
          const ribbons = sejourEvents
            .filter(e => e.household?.id === h.id)
            .map(e => {
              const startIdx = days.findIndex(d => d.isSame(e.startTime, 'day'));
              const endDay = e.endTime ?? e.startTime;
              const endIdx = days.findIndex(d => d.isSame(endDay, 'day'));
              const from = startIdx < 0 ? 0 : startIdx;
              const to = endIdx < 0 ? daysInMonth - 1 : endIdx;
              const span = to - from + 1;
              if (span <= 0) return null;
              return {
                key: e.localId,
                from,
                span,
                label: e.name ?? h.name,
              };
            })
            .filter((r): r is { key: string; from: number; span: number; label: string } => !!r);

          return (
            <div key={h.id} style={rowStyle}>
              <div style={rowLabelStyle}>{h.name}</div>
              {days.map((d, idx) => {
                const dow = d.day();
                return (
                  <div
                    key={d.format('YYYY-MM-DD')}
                    style={dayCellStyle(idx, dow === 0 || dow === 6)}
                  />
                );
              })}
              {ribbons.map(r => (
                <div
                  key={r.key}
                  style={{
                    ...ribbonStyleFor(h.color),
                    gridColumn: `${r.from + 2} / span ${r.span}`,
                  }}
                >
                  {r.label}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Legend + QR */}
      <div
        style={{
          padding: '12px 20px',
          borderTop: `1.5px solid ${BORDER}`,
          marginTop: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 10,
            color: INK,
          }}
        >
          {households.map(h => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  background: h.color,
                  border: `1px solid ${BORDER}`,
                }}
              />
              <span>{h.name}</span>
            </div>
          ))}
        </div>
        <div
          style={{ padding: 3, border: `1px solid ${BORDER}`, lineHeight: 0, background: '#fff' }}
        >
          <QRCodeSVG value={exportUrl} size={60} level="M" fgColor={INK} bgColor="#FFFFFF" />
        </div>
      </div>
    </div>
  );
};

export default PrintableStaysTimeline;
